import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ReviewServices } from "@/services/bedrock";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";

export type CoachReviewsInfiniteQueryResult = ReturnType<typeof useCoachReviewsInfiniteQuery>;

export function useCoachReviewsInfiniteQuery() {
  const { toQueryParams } = useCoachReviewsFilters();
  const filterParams = useMemo(() => toQueryParams(), [toQueryParams]);

  const query = useInfiniteQuery({
    queryKey: ["reviews", "infinite", filterParams],
    queryFn: ({ pageParam }) =>
      ReviewServices.getReviews({
        ...filterParams,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    // Keep filter caches warm across tab switches, but don't freeze forever.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const reviews = useMemo(() => query.data?.pages.flatMap(page => page.content) ?? [], [query.data?.pages]);
  const totalCount = query.data?.pages[0]?.pagination?.totalCount;
  const isRefreshing = query.isFetching && !query.isFetchingNextPage && reviews.length > 0;

  return {
    ...query,
    reviews,
    totalCount,
    filterParams,
    isRefreshing,
  };
}
